"use client";

import { useEffect, useState } from "react";
import { useStoredUser } from "@/hooks/useStoredUser";

type Props = {
  itemName: string;
};

export function AppraisalInquiry({ itemName }: Props) {
  const storedUser = useStoredUser();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!storedUser) return;
    if (storedUser.email && !email) setEmail(storedUser.email);
    if (storedUser.firstName && !name) setName(storedUser.firstName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  const hasStoredContact = Boolean(storedUser?.email && storedUser?.firstName);

  const handleSubmit = async () => {
    setError("");

    if (!email.trim()) {
      setError("Please provide your email address.");
      return;
    }
    if (!name.trim()) {
      setError("Please provide your name.");
      return;
    }

    const parts: string[] = [];
    parts.push("--- APPRAISAL INQUIRY ---");
    parts.push(`Piece: ${itemName}`);
    if (message.trim()) {
      parts.push("");
      parts.push(message.trim());
    }

    setSubmitting(true);

    try {
      const res = await fetch("/api/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firstName: name.trim(),
          emailBody: parts.join("\n"),
          pieceNames: [itemName],
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-3 border border-slate-200 bg-white/80 p-5">
        <div className="flex items-center gap-3">
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5 flex-shrink-0 text-emerald-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <p className="text-sm text-slate-700">
            Thank you for your inquiry. We will be in touch shortly regarding the appraisal of this piece.
          </p>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-2.5 rounded-sm border border-slate-200 px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        Inquire About Appraisal
      </button>
    );
  }

  return (
    <div className="space-y-4 border border-slate-200 bg-white/80 p-5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
          Appraisal Inquiry
        </p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-1 text-slate-400 transition hover:text-slate-600"
          aria-label="Close appraisal inquiry"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <p className="text-sm text-slate-600">
        Interested in having this piece professionally appraised before purchase? Fill out the form below and we will follow up with details.
      </p>

      {hasStoredContact ? (
        <p className="text-sm text-slate-600">
          Sending as <span className="font-semibold text-slate-900">{name}</span>{" "}
          <span className="text-slate-400">({email})</span>
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="appraisal-name" className="sr-only">Name</label>
            <input
              id="appraisal-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div>
            <label htmlFor="appraisal-email" className="sr-only">Email</label>
            <input
              id="appraisal-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
        </div>
      )}

      <div>
        <label htmlFor="appraisal-message" className="sr-only">Message (optional)</label>
        <textarea
          id="appraisal-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Any additional details or questions (optional)"
          className="min-h-[80px] w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
        />
      </div>

      {error && <p className="text-center text-sm text-red-600">{error}</p>}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full rounded-sm bg-[#c4a668] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {submitting ? "Sending..." : "Send Inquiry"}
      </button>
    </div>
  );
}
